import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Button,
  Form,
  Label,
  FormFeedback,
  Input,
  FormText,
} from "reactstrap"
import { Link, useNavigate, useParams } from "react-router-dom"
import leftArrow from "../../assets/images/icons/left-arrow.svg"
import LoaderComponent from "components/loader"
import Spinners from "components/Common/Spinner"
import * as Yup from "yup"
import { useFormik } from "formik"
import toast from "react-hot-toast"
import {
  useEditVideoMutation,
  useLazyGetVideoQuery,
} from "store/api/videos/investmentVideos"
import VideoUploader from "./videoUploader"
import KycDocumentUpload from "pages/KYC/kycDocumentUpload"
import UploadedFileBox from "pages/KYC/uploadedFileBox"
import Breadcrumb from "components/Common/Breadcrumb"
import { sanitizeIntegerInput } from "helpers/common"

const VideoEdit = ({ user_type }) => {
  const { id } = useParams()
  const [getResult, { isFetching }] = useLazyGetVideoQuery()
  const [updateFaq, { isFetching: isUpdating }] = useEditVideoMutation()
  const [details, setDetails] = useState(null)
  const navigate = useNavigate()

  document.title = "Video Details | Prachay"

  const getFaqData = async () => {
    try {
      const { success, data } = await getResult({ id }).unwrap()
      if (success) {
        setDetails(data)
        validation.setFieldValue("title", data?.title)
        validation.setFieldValue("status", data?.status)
        validation.setFieldValue("sequence", data?.sequence)
      }
    } catch (error) {}
  }

  const editFaq = async (values) => {
    try {
      const { success, message, data } = await updateFaq({
        id,
        ...values,
        user_type,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "FaqSuccess" })
        navigate(
          user_type === "INVESTOR"
            ? "/resources/investment-videos"
            : "/wp-videos"
        )
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  useEffect(() => {
    if (id) {
      getFaqData()
    }
  }, [id])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      status: "",
      video: "",
      sequence: "",
      thumbnail: "",
    },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      await editFaq(values)
    },
  })

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center gap-2  pt-3">
            <Link to={"/videos"}>
              <img src={leftArrow} alt="" />
            </Link>
            <div className="page-title-name">Investment Video</div>
          </div>
          <Row>
            <Col xl={12}>
              <Breadcrumb title={"Update Video"} />
              <Card>
                <CardBody>
                  {isFetching ? (
                    <LoaderComponent />
                  ) : (
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <div className="d-flex align-items-center mb-4 justify-content-between"></div>
                      <Row className="mb-2">
                        <Col sm={12} className="mb-3">
                          <Label className="form-label">
                            Title
                            <span className="error">*</span>
                          </Label>
                          <Input
                            name="title"
                            className="form-control"
                            placeholder={"Enter Title"}
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.title}
                            invalid={
                              validation.touched.title &&
                              validation.errors.title
                            }
                          />
                          {validation.touched.title &&
                          validation.errors.title ? (
                            <FormFeedback type="invalid">
                              {validation.errors.title}
                            </FormFeedback>
                          ) : null}
                        </Col>

                        <Col sm={6} className="mb-2">
                          <Label className="form-label">
                            Sequence
                            <span className="error">*</span>
                          </Label>
                          <Input
                            name="sequence"
                            className="form-control"
                            placeholder={"Enter Sequence"}
                            type="text"
                            onChange={(e) =>
                              sanitizeIntegerInput(
                                e,
                                validation.setFieldValue,
                                "sequence"
                              )
                            }
                            onBlur={validation.handleBlur}
                            value={validation.values.sequence}
                            invalid={
                              validation.touched.sequence &&
                              validation.errors.sequence
                            }
                          />
                          {validation.touched.sequence &&
                          validation.errors.sequence ? (
                            <FormFeedback type="invalid">
                              {validation.errors.sequence}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col xl={6} className="mb-2">
                          <Label className="form-label">
                            Status
                            <span className="error">*</span>
                          </Label>
                          <select
                            name="status"
                            className="form-select pageSize mb-2"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.status}
                          >
                            <option>Select Status</option>
                            {[
                              { label: "ACTIVE", value: "ACTIVE" },
                              { label: "DISABLE", value: "DISABLE" },
                            ].map((item, index) => (
                              <option key={index} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                          {validation.touched.status &&
                          validation.errors.status ? (
                            <FormFeedback type="invalid">
                              {validation.errors.status}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col sm={6}>
                          <Label className="col-form-label">
                            Video
                            <span className="error">*</span>
                          </Label>
                          <VideoUploader
                            value={validation?.values?.video}
                            placeholder="Select Video"
                            fieldKey={"video"}
                            validation={validation}
                          />

                          <FormText className="text-info">
                            *Note:Leave Blank if do not want to update
                          </FormText>
                          {details?.video_url && (
                            <div>
                              <Label className="form-label">
                                Video Preview
                              </Label>
                              <video
                                controls
                                width="100%"
                                style={{
                                  borderRadius: "10px",
                                  backgroundColor: "#000",
                                }}
                              >
                                <source
                                  src={details.video_url}
                                  type="video/mp4"
                                />
                              </video>
                            </div>
                          )}
                        </Col>
                        <Col sm={6}>
                          <Label className="col-form-label">
                            Poster Image
                            <span className="error">*</span>
                          </Label>
                          <KycDocumentUpload
                            value={validation?.values?.thumbnail}
                            placeholder="Select Poster Image"
                            fieldKey={"thumbnail"}
                            validation={validation}
                            extensionAllowed={["image/jpeg", "image/png"]}
                            typeError="Please upload files in JPG, PNG format only"
                          />

                          <FormText className="text-info">
                            *Note:Leave Blank if do not want to update
                          </FormText>
                          {details?.thumbnail_url && (
                            <UploadedFileBox
                              label={"Uploaded Poster"}
                              href={details?.thumbnail_url}
                            />
                          )}
                        </Col>
                      </Row>

                      <Row className="justify-content-end mt-4">
                        <Col sm={12}>
                          <div className="d-flex justify-content-end align-items-center gap-3">
                            <Button
                              type="submit"
                              className="btn btn-primary w-md"
                              disabled={isUpdating}
                            >
                              {isUpdating ? <Spinners /> : "Update Video"}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default VideoEdit
